<template>
  <generic-modal
    :title="$t('sms code verification')"
    ref="generic_modal"
    :is_closable="false"
  >
    <div class="box">
      <!-- Mensaje informativo -->
      <div class="notification is-info is-light">
        <div class="media">
          <div class="media-left">
            <span class="icon is-large has-text-info">
              <i class="fa fa-mobile fa-2x"></i>
            </span>
          </div>
          <div class="media-content">
            <p>
              {{ $t("we will send a verification code to phone number") }}:
              <br />
              <strong class="is-size-5">{{ getPhone() }}</strong>
            </p>
          </div>
        </div>
      </div>

      <!-- Opciones principales - Siempre visibles -->
      <div class="columns is-mobile">
        <div class="column">
          <button
            class="button is-primary is-fullwidth"
            @click="send_sms_code"
            :class="{ 'is-loading': meta.sending_code }"
            :disabled="meta.sending_code"
          >
            <span class="icon">
              <i class="fa fa-paper-plane"></i>
            </span>
            <span>{{ $t("send new code") }}</span>
          </button>
        </div>
        <div class="column">
          <button
            class="button is-light is-fullwidth"
            @click="meta.show_code_input = true"
            :disabled="meta.show_code_input"
          >
            <span class="icon">
              <i class="fa fa-keyboard-o"></i>
            </span>
            <span>{{ $t("enter code") }}</span>
          </button>
        </div>
      </div>

      <!-- Mensaje de código enviado -->
      <div v-if="meta.code_sent" class="notification is-success is-light">
        <div class="media">
          <div class="media-left">
            <span class="icon has-text-success">
              <i class="fa fa-check-circle"></i>
            </span>
          </div>
          <div class="media-content">
            <p>{{ $t("verification code sent successfully") }}</p>
          </div>
        </div>
      </div>

      <!-- Sección para ingresar código -->
      <div
        v-if="meta.code_sent || meta.show_code_input"
        class="code-input-section"
      >
        <div class="field">
          <label class="label">{{ $t("verification code") }}</label>
          <div class="control">
            <!-- Input mejorado para código -->
            <div class="code-input-container">
              <input
                v-for="(digit, index) in 6"
                :key="index"
                :ref="`codeInput${index}`"
                class="code-digit-input"
                type="text"
                maxlength="1"
                :value="getDigit(index)"
                @input="onDigitInput(index, $event)"
                @keydown="onKeyDown(index, $event)"
                @paste="onPaste($event)"
              />
            </div>
          </div>
          <p v-if="error" class="help is-danger">
            <i class="fa fa-exclamation-triangle"></i>
            {{ error }}
          </p>
          <p v-else class="help">
            {{ $t("enter the 6-digit verification code") }}
          </p>
        </div>

        <div class="field">
          <div class="control">
            <button
              class="button is-success is-fullwidth is-large"
              @click="verify_code"
              :class="{ 'is-loading': meta.verifying_code }"
              :disabled="meta.code.length !== 6 || meta.verifying_code"
            >
              <span class="icon">
                <i class="fa fa-check-circle"></i>
              </span>
              <span>{{ $t("verify code") }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <template v-slot:footer>
      <!-- <button class="button" @click="close_modal">
        <span class="icon">
          <i class="fa fa-times"></i>
        </span>
        <span>{{ $t("cancel") }}</span>
      </button> -->
    </template>
  </generic-modal>
</template>

<script>
import GenericModal from "@/components/generic_modal/Index.vue";
import { post, put, get, success_message, error_message } from "@/utils";
import moment from "moment";

export default {
  name: "SMSVerificationModal",
  components: { GenericModal },
  data() {
    return {
      account_request: null,
      error: "",
      meta: {
        code: "",
        code_sent: false,
        sending_code: false,
        verifying_code: false,
        show_code_input: false,
      },
    };
  },
  computed: {},
  methods: {
    show(account_request) {
      let v = this;
      v.account_request = account_request;
      v.$refs.generic_modal.show_modal();
    },
    close_modal() {
      let v = this;
      v.$refs.generic_modal.close_modal();
      v.set_initial_state();
    },
    set_initial_state() {
      this.meta = {
        code: "",
        code_sent: false,
        sending_code: false,
        verifying_code: false,
        show_code_input: false,
      };
      this.account_request = null;
      this.error = "";
    },

    // Obtener teléfono del destinatario
    getPhone() {
      return this.account_request?.destinatary?.phone || "N/A";
    },

    // Métodos para el input de código mejorado
    getDigit(index) {
      return this.meta.code[index] || "";
    },

    onDigitInput(index, event) {
      const value = event.target.value.replace(/[^0-9]/g, "");

      if (value.length > 0) {
        // Actualizar el código
        let newCode = this.meta.code.split("");
        newCode[index] = value[value.length - 1]; // Solo el último dígito
        this.meta.code = newCode.join("").substring(0, 6);

        // Mover al siguiente input
        if (index < 5 && value.length > 0) {
          this.$nextTick(() => {
            const nextInput = this.$refs[`codeInput${index + 1}`];
            if (nextInput && nextInput[0]) {
              nextInput[0].focus();
            }
          });
        }
      }

      this.clearError();
    },

    onKeyDown(index, event) {
      // Backspace: borrar y mover al anterior
      if (event.key === "Backspace") {
        if (this.getDigit(index) === "" && index > 0) {
          // Si está vacío, mover al anterior
          const prevInput = this.$refs[`codeInput${index - 1}`];
          if (prevInput && prevInput[0]) {
            prevInput[0].focus();
          }
        } else {
          // Borrar el dígito actual
          let newCode = this.meta.code.split("");
          newCode[index] = "";
          this.meta.code = newCode.join("").replace(/\s/g, "");
        }
      }

      // Arrow keys para navegación
      if (event.key === "ArrowLeft" && index > 0) {
        const prevInput = this.$refs[`codeInput${index - 1}`];
        if (prevInput && prevInput[0]) {
          prevInput[0].focus();
        }
      }

      if (event.key === "ArrowRight" && index < 5) {
        const nextInput = this.$refs[`codeInput${index + 1}`];
        if (nextInput && nextInput[0]) {
          nextInput[0].focus();
        }
      }
    },

    onPaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData
        .getData("text")
        .replace(/[^0-9]/g, "");

      if (pastedData.length > 0) {
        this.meta.code = pastedData.substring(0, 6);

        // Enfocar el último input con contenido o el primero vacío
        const focusIndex = Math.min(pastedData.length, 5);
        this.$nextTick(() => {
          const targetInput = this.$refs[`codeInput${focusIndex}`];
          if (targetInput && targetInput[0]) {
            targetInput[0].focus();
          }
        });
      }
    },

    clearError() {
      if (this.error && this.meta.code.length > 0) {
        this.error = "";
      }
    },

    // Método para enviar código SMS
    send_sms_code() {
      let v = this;

      if (!v.account_request?.destinatary?.phone) {
        v.error = v.$t("phone number not available");
        error_message(v.error);
        return;
      }

      v.meta.sending_code = true;
      v.error = "";

      const payload = {
        uuid: v.account_request.uuid,
        // phone: v.account_request.destinatary.phone,
      };

      post("/account-request/personal-savings/complete/send-sms-code", payload)
        .then((/* response */) => {
          v.meta.code_sent = true;
          // success_message(v.$t("verification code sent successfully"));
          // Clear previous code
          v.meta.code = "";
          // Show code input section
          v.meta.show_code_input = true;
        })
        .catch((error) => {
          console.error("Error sending SMS code:", error);
          v.error = error.message || v.$t("error sending verification code");
          error_message(v.error);
        })
        .finally(() => {
          v.meta.sending_code = false;
        });
    },

    // Método para verificar código
    verify_code() {
      let v = this;

      if (!v.meta.code || v.meta.code.length !== 6) {
        v.error = v.$t("please enter a valid 6-digit code");
        return;
      }

      v.meta.verifying_code = true;
      v.error = "";

      const payload = {
        uuid: v.account_request.uuid,
        code: v.meta.code,
      };

      post("/account-request/personal-savings/complete/verify-code", payload)
        .then((/* response */) => {
          v.$emit("verification-completed", v.account_request);
          v.close_modal();
        })
        .catch((error) => {
          if (
            error.status === 500 &&
            error.data &&
            error.data.includes("Invalid code")
          ) {
            v.error = v.$t("invalid verification code");
          } else if (
            error.status === 400 &&
            error.data &&
            error.data.includes("expired")
          ) {
            v.error = v.$t(
              "the verification code has expired, please request a new one"
            );
          } else {
            v.error = error.message || v.$t("error verifying code");
          }
          console.error("Error verifying SMS code:", error);
          error_message(v.error);
        })
        .finally(() => {
          v.meta.verifying_code = false;
        });
    },

    // Validar input del código (simplificado)
    validateCodeInput() {
      // Este método ya no es necesario con el nuevo sistema
      // pero lo mantenemos por compatibilidad
      this.meta.code = this.meta.code.replace(/[^0-9]/g, "").substring(0, 6);
      this.clearError();
    },
  },
};
</script>

<style scoped>
.code-input-section {
  margin-top: 1.5rem;
}

.code-input-container {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin: 1rem 0;
}

.code-digit-input {
  width: 3rem;
  height: 3rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: bold;
  border: 2px solid #dbdbdb;
  border-radius: 8px;
  background: white;
  transition: all 0.2s ease;
  font-family: "Courier New", monospace;
}

.code-digit-input:focus {
  outline: none;
  border-color: #3273dc;
  box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
  transform: scale(1.05);
}

.code-digit-input:not(:placeholder-shown) {
  border-color: #48c774;
  background-color: #f0fdf4;
}

/* Media query para móviles */
@media screen and (max-width: 768px) {
  .code-digit-input {
    width: 2.5rem;
    height: 2.5rem;
    font-size: 1.25rem;
  }

  .code-input-container {
    gap: 0.25rem;
  }
}

/* Mejorar el mensaje informativo */
.notification.is-info.is-light .media-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Botones responsivos */
@media screen and (max-width: 768px) {
  .columns.is-mobile .column {
    padding: 0.25rem;
  }
}
</style>
